import React, { useEffect, useRef, useState } from 'react';
import { Streamer, getStreamers, getTeams, createStreamer, importExcel, deleteStreamer, getPeriods, getSettings } from './api';
import TeamView from './TeamView';
import LiveAvatar from './LiveAvatar';

interface Props {
  onSelect: (streamer: Streamer) => void;
  selected: Streamer | null;
  refreshKey: number;
  onSelectedUpdated: (streamer: Streamer) => void;
  isAdmin: boolean;
  onStreamerCreated?: () => void;
  isMobile?: boolean;
}

type SortKey = 'name' | 'username' | 'diamonds' | 'duration' | 'days' | 'remu_niveau' | 'remu_activite' | 'remu_progressif';

interface Objectives { days: number; hours: number; diamonds: number; }

interface TeamSummaryData {
  totalDiamonds: number;
  totalRemuNiveau: number;
  totalRemuActivite: number;
  totalRemuProgressif: number;
  teamCount: number;
  period?: string;
}

export default function StreamerList({ onSelect, selected, refreshKey, onSelectedUpdated, isAdmin, onStreamerCreated, isMobile }: Props) {
  const [tab, setTab] = useState<'teams' | 'streamers'>('streamers');
  const [periods, setPeriods] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [streamers, setStreamers] = useState<Streamer[]>([]);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [creating, setCreating] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [objectives, setObjectives] = useState<Objectives>({ days: 8, hours: 20, diamonds: 750 });
  const [filterLive, setFilterLive] = useState(false);
  const [filterObjAny, setFilterObjAny] = useState(false);
  const [filterObjAll, setFilterObjAll] = useState(false);
  const [teamSummary, setTeamSummary] = useState<TeamSummaryData | null>(null);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const [showImport, setShowImport] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDragging, setImportDragging] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; created: number; updated: number; skipped: number; deleted: number; period: string } | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = async (period?: string) => {
    const res = await getStreamers(period || selectedPeriod || undefined);
    setStreamers(res.data);
    if (selected) {
      const updated = res.data.find(s => s.id === selected.id);
      if (updated) onSelectedUpdated(updated);
    }
  };

  const loadPeriods = async () => {
    try {
      const res = await getPeriods();
      // Dédupliquer : garder un seul import par mois calendaire (le plus récent en premier)
      const seen = new Set<string>();
      const deduped = res.data.filter(p => {
        const monthKey = p.match(/^(\d{4}-\d{2})/)?.[1] ?? p;
        if (seen.has(monthKey)) return false;
        seen.add(monthKey);
        return true;
      });
      setPeriods(deduped);
      if (deduped.length > 0 && !selectedPeriod) {
        setSelectedPeriod(deduped[0]);
      }
    } catch {}
  };

  useEffect(() => {
    loadPeriods();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getSettings().then(r => {
      const d = r.data;
      setObjectives({
        days:     parseInt(d['objective_days']     || '8')   || 8,
        hours:    parseInt(d['objective_hours']    || '20')  || 20,
        diamonds: parseInt(d['objective_diamonds'] || '750') || 750,
      });
    }).catch(() => {});
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [refreshKey, selectedPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch team summary independently so the banner shows on Streamers tab too
  useEffect(() => {
    if (!selectedPeriod) return;
    getTeams(selectedPeriod).then(r => {
      const ts = r.data;
      if (ts.length > 0) {
        setTeamSummary({
          totalDiamonds:       ts.reduce((s, t) => s + (t.total_diamonds        || 0), 0),
          totalRemuNiveau:     ts.reduce((s, t) => s + (t.total_remu_niveau     || 0), 0),
          totalRemuActivite:   ts.reduce((s, t) => s + (t.total_remu_activite   || 0), 0),
          totalRemuProgressif: ts.reduce((s, t) => s + (t.total_remu_progressif || 0), 0),
          teamCount: ts.length,
          period: ts[0]?.period ?? undefined,
        });
      }
    }).catch(() => {});
  }, [selectedPeriod, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const countMet = (s: Streamer) => [
    (s.live_days ?? 0) >= objectives.days,
    (s.live_duration_minutes ?? 0) >= objectives.hours * 60,
    (s.diamonds ?? 0) >= objectives.diamonds,
  ].filter(Boolean).length;

  const filtered = streamers
    .filter(s =>
      s.display_name.toLowerCase().includes(search.toLowerCase()) ||
      s.username.toLowerCase().includes(search.toLowerCase())
    )
    .filter(s => !filterLive || !!s.tiktok_live)
    .filter(s => {
      if (!filterObjAny && !filterObjAll) return true;
      const met = countMet(s);
      if (filterObjAll) return met === 3;
      return met >= 1; // filterObjAny
    })
    .sort((a, b) => {
      let av: any, bv: any;
      switch (sortKey) {
        case 'name':     av = a.display_name.toLowerCase(); bv = b.display_name.toLowerCase(); break;
        case 'username': av = a.username.toLowerCase();     bv = b.username.toLowerCase();     break;
        case 'diamonds': av = a.diamonds ?? -1;             bv = b.diamonds ?? -1;             break;
        case 'duration': av = a.live_duration_minutes ?? -1;bv = b.live_duration_minutes ?? -1;break;
        case 'days':          av = a.live_days ?? -1;          bv = b.live_days ?? -1;          break;
        case 'remu_niveau':   av = a.remu_niveau ?? -1;       bv = b.remu_niveau ?? -1;       break;
        case 'remu_activite': av = a.remu_activite ?? -1;     bv = b.remu_activite ?? -1;     break;
        case 'remu_progressif': av = a.remu_progressif ?? -1; bv = b.remu_progressif ?? -1;   break;
        default:              av = ''; bv = '';
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleImportFile = async (file: File | undefined) => {
    if (!file) return;
    setImportError(null);
    setImportResult(null);
    setImporting(true);
    try {
      const r = await importExcel(file);
      setImportResult(r.data);
      load();
    } catch (e: any) {
      setImportError(e?.response?.data?.detail || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleCreate = async () => {
    if (!newUsername.trim() || !newDisplayName.trim()) return;
    setCreating(true);
    try {
      await createStreamer(newUsername.trim(), newDisplayName.trim());
      setNewUsername(''); setNewDisplayName('');
      setShowCreate(false);
      onStreamerCreated?.();
      load();
    } catch {}
    setCreating(false);
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>

      {/* ── Header ── */}
      <div style={{ padding: isMobile ? '16px 16px 0' : '24px 32px 0', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: isMobile ? 12 : 16 }}>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? 18 : 22, fontWeight: 700, lineHeight: 1.2 }}>Streamers</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Sélecteur de période */}
            {periods.length > 0 && (
              <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer', outline: 'none' }}>
                {periods.map(p => (
                  <option key={p} value={p}>{formatPeriodLabel(p)}</option>
                ))}
              </select>
            )}
            {isAdmin && (
              <>
                <button onClick={() => { setShowImport(true); setImportResult(null); setImportError(null); }} style={{ ...btnGhost, fontSize: isMobile ? 11 : 12, padding: isMobile ? '6px 10px' : '8px 14px' }}>
                  ↑ Excel
                </button>
                <button onClick={() => setShowCreate(true)} style={{ ...btnPrimary, fontSize: isMobile ? 12 : 13, padding: isMobile ? '7px 12px' : '9px 18px' }}>
                  + Nouveau
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Summary banner (données équipes) ── */}
        {teamSummary && (() => {
          const { totalDiamonds, totalRemuNiveau, totalRemuActivite, totalRemuProgressif, teamCount } = teamSummary;
          const totalRemu = totalRemuNiveau + totalRemuActivite + totalRemuProgressif;
          return isMobile ? (
            <div style={{ paddingTop: 10, paddingBottom: 6 }}>
              {/* Ligne 1 : Équipes + Total agence */}
              <div style={{ display: 'flex', gap: 20, marginBottom: totalRemu > 0 ? 6 : 0 }}>
                <div><div style={summaryLabelStyle}>Équipes</div><div style={{ color: '#0f172a', fontSize: 16, fontWeight: 700 }}>{teamCount}</div></div>
                <div><div style={summaryLabelStyle}>Total agence</div><div style={{ color: '#0f172a', fontSize: 16, fontWeight: 700 }}>{fmtNum(totalDiamonds)} 💎</div></div>
              </div>
              {/* Ligne 2 : rému (si > 0) */}
              {totalRemu > 0 && (
                <div style={{ display: 'flex', gap: 14 }}>
                  <div><div style={summaryLabelStyle}>Montée niv.</div><div style={{ color: '#15803d', fontSize: 13, fontWeight: 700 }}>${totalRemuNiveau.toFixed(2)}</div></div>
                  <div><div style={summaryLabelStyle}>Activité</div><div style={{ color: '#2563eb', fontSize: 13, fontWeight: 700 }}>${totalRemuActivite.toFixed(2)}</div></div>
                  {isAdmin && <div><div style={summaryLabelStyle}>Progressif</div><div style={{ color: '#7c3aed', fontSize: 13, fontWeight: 700 }}>${totalRemuProgressif.toFixed(2)}</div></div>}
                </div>
              )}
            </div>
          ) : (
            <div style={{ paddingTop: 12, paddingBottom: 12, display: 'flex', gap: 28, alignItems: 'center', flexWrap: 'wrap' }}>
              <div><div style={summaryLabelStyle}>Total agence</div><div style={{ color: '#0f172a', fontSize: 18, fontWeight: 700, marginTop: 1 }}>{fmtNum(totalDiamonds)} 💎</div></div>
              {totalRemu > 0 && <>
                <div><div style={summaryLabelStyle}>Montée niv.</div><div style={{ color: '#15803d', fontSize: 16, fontWeight: 700, marginTop: 1 }}>${totalRemuNiveau.toFixed(2)}</div></div>
                <div><div style={summaryLabelStyle}>Activité</div><div style={{ color: '#2563eb', fontSize: 16, fontWeight: 700, marginTop: 1 }}>${totalRemuActivite.toFixed(2)}</div></div>
                {isAdmin && <div><div style={summaryLabelStyle}>Progressif</div><div style={{ color: '#7c3aed', fontSize: 16, fontWeight: 700, marginTop: 1 }}>${totalRemuProgressif.toFixed(2)}</div></div>}
              </>}
              <div><div style={summaryLabelStyle}>Équipes</div><div style={{ color: '#0f172a', fontSize: 18, fontWeight: 700, marginTop: 1 }}>{teamCount}</div></div>
            </div>
          );
        })()}

        {/* ── Search + filtres (toujours visibles) ── */}
        <div style={{ paddingTop: teamSummary ? 4 : 12, paddingBottom: 0 }}>
          {isMobile ? (
            /* Mobile : recherche pleine largeur, filtres sur la ligne dessous */
            <>
              <div style={{ position: 'relative', marginBottom: 6 }}>
                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
                </svg>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={tab === 'teams' ? 'Rechercher une équipe…' : 'Rechercher…'}
                  style={{ ...inputLight, paddingLeft: 32, padding: '7px 12px 7px 32px', width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 4 }}>
                <FilterChip active={filterLive} color="#dc2626" bg="#fef2f2" small onClick={() => setFilterLive(v => !v)}>🔴 En live</FilterChip>
                <FilterChip active={filterObjAny && !filterObjAll} color="#ea580c" bg="#fff7ed" small
                  onClick={() => { setFilterObjAny(v => { const next = !v; if (next) setFilterObjAll(false); return next; }); }}>⚠️ ≥1 objectif</FilterChip>
                <FilterChip active={filterObjAll} color="#16a34a" bg="#f0fdf4" small
                  onClick={() => { setFilterObjAll(v => { const next = !v; if (next) setFilterObjAny(false); return next; }); }}>✅ Complets</FilterChip>
              </div>
            </>
          ) : (
            /* Desktop : recherche + filtres sur la même ligne */
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <div style={{ position: 'relative', flex: '0 0 260px' }}>
                <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="M21 21l-4.35-4.35" />
                </svg>
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={tab === 'teams' ? 'Rechercher une équipe…' : 'Rechercher…'}
                  style={{ ...inputLight, paddingLeft: 32 }} />
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <FilterChip active={filterLive} color="#dc2626" bg="#fef2f2" onClick={() => setFilterLive(v => !v)}>🔴 En live</FilterChip>
                <FilterChip active={filterObjAny && !filterObjAll} color="#ea580c" bg="#fff7ed"
                  onClick={() => { setFilterObjAny(v => { const next = !v; if (next) setFilterObjAll(false); return next; }); }}>⚠️ ≥1 objectif</FilterChip>
                <FilterChip active={filterObjAll} color="#16a34a" bg="#f0fdf4"
                  onClick={() => { setFilterObjAll(v => { const next = !v; if (next) setFilterObjAny(false); return next; }); }}>✅ Objectifs complets</FilterChip>
              </div>
            </div>
          )}
        </div>

        {/* Tabs — desktop uniquement (mobile = bottom nav) */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: 0, borderBottom: 'none', marginTop: 2 }}>
            {(['streamers', 'teams'] as const).map(t => (
              <button key={t} onClick={() => setTab(t)}
                style={{ padding: '9px 20px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: tab === t ? 600 : 400, color: tab === t ? '#2563eb' : '#64748b', borderBottom: `2px solid ${tab === t ? '#2563eb' : 'transparent'}`, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                {t === 'teams' ? '🏆 Équipes' : `👥 Streamers${streamers.length ? ` (${streamers.length})` : ''}`}
              </button>
            ))}
          </div>
        )}

        {/* En-têtes colonnes desktop (onglet Streamers uniquement) */}
        {tab === 'streamers' && !isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 130px 110px 70px 60px 80px 80px 80px 60px' : '1fr 130px 110px 70px 60px 80px 80px 60px', gap: 8, padding: '10px 16px 8px', marginTop: 4 }}>
            {([ ['name','Streamer'], ['username','@TikTok'], ['diamonds','💎 Diamants'], ['duration','Durée'], ['days','Jours'] ] as [SortKey, string][]).map(([key, label]) => (
              <span key={key} onClick={() => handleSort(key)}
                style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                {label}
                <span style={{ opacity: sortKey === key ? 1 : 0.25, fontSize: 10 }}>
                  {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                </span>
              </span>
            ))}
            {((isAdmin ? ['remu_niveau', 'remu_activite', 'remu_progressif'] : ['remu_niveau', 'remu_activite']) as SortKey[]).map((key, i) => (
              <span key={key} onClick={() => handleSort(key)}
                style={{ ...thStyle, cursor: 'pointer', userSelect: 'none', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                {['Montée niv.', 'Activité', 'Progressif'][i]}
                <span style={{ opacity: sortKey === key ? 1 : 0.25, fontSize: 10 }}>
                  {sortKey === key ? (sortDir === 'asc' ? '▲' : '▼') : '▲'}
                </span>
              </span>
            ))}
            <span style={thStyle}></span>
          </div>
        )}
      </div>

      {/* ── Teams tab ── */}
      {tab === 'teams' && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: isMobile ? 58 : 0 }}>
          <TeamView
            refreshKey={refreshKey}
            isMobile={isMobile}
            activePeriod={selectedPeriod}
            isAdmin={isAdmin}
            objectives={objectives}
            searchTerm={search}
            filterLive={filterLive}
            filterObjAny={filterObjAny}
            filterObjAll={filterObjAll}
            onSummaryChange={setTeamSummary}
            onSelectStreamer={id => {
              const s = streamers.find(x => x.id === id);
              if (s) onSelect(s);
            }}
          />
        </div>
      )}

      {/* ── Streamers tab ── */}
      {tab === 'streamers' && (
      <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '12px' : '8px 32px 24px', paddingBottom: isMobile ? '70px' : undefined }}>
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.4 }}>👥</div>
            <div style={{ fontSize: 14 }}>{search ? 'Aucun résultat' : 'Aucun streamer'}</div>
          </div>
        ) : isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map(s => <StreamerCard key={s.id} streamer={s} onSelect={onSelect} objectives={objectives} />)}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map(s => (
              <StreamerRow key={s.id} streamer={s} onSelect={onSelect} isAdmin={isAdmin} objectives={objectives}
                onDelete={async () => {
                  if (!window.confirm(`Supprimer "${s.display_name}" ? Cette action est irréversible.`)) return;
                  await deleteStreamer(s.id);
                  load();
                }} />
            ))}
          </div>
        )}
      </div>
      )}

      {/* ── Mobile bottom nav ── */}
      {isMobile && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#fff', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'stretch', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <button onClick={() => setTab('streamers')} style={{ flex: 1, padding: '8px 4px 6px', border: 'none', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: tab === 'streamers' ? '#2563eb' : '#64748b', cursor: 'pointer', borderTop: `2px solid ${tab === 'streamers' ? '#2563eb' : 'transparent'}` }}>
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span style={{ fontSize: 10, fontWeight: tab === 'streamers' ? 600 : 400 }}>Streamers</span>
          </button>
          <button onClick={() => setTab('teams')} style={{ flex: 1, padding: '8px 4px 6px', border: 'none', background: 'transparent', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, color: tab === 'teams' ? '#2563eb' : '#64748b', cursor: 'pointer', borderTop: `2px solid ${tab === 'teams' ? '#2563eb' : 'transparent'}` }}>
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <span style={{ fontSize: 10, fontWeight: tab === 'teams' ? 600 : 400 }}>Équipes</span>
          </button>
        </div>
      )}

      {/* ── Modal import Excel ── */}
      {showImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={e => { if (e.target === e.currentTarget) { setShowImport(false); setImportResult(null); setImportError(null); } }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 18 }}>Importer Excel Backstage</div>
            <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>
              Importez l'export Excel quotidien de TikTok Backstage. Les streamers manquants seront créés automatiquement.
            </p>

            {/* Drop zone */}
            {!importResult && (
              <div
                onDragOver={e => { e.preventDefault(); setImportDragging(true); }}
                onDragLeave={() => setImportDragging(false)}
                onDrop={e => { e.preventDefault(); setImportDragging(false); handleImportFile(e.dataTransfer.files[0]); }}
                onClick={() => fileInputRef.current?.click()}
                style={{ border: `2px dashed ${importDragging ? '#2563eb' : '#cbd5e1'}`, borderRadius: 10, padding: '32px 16px', textAlign: 'center', cursor: 'pointer', background: importDragging ? '#eff6ff' : '#f8fafc', transition: 'all 0.15s' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                <div style={{ color: '#374151', fontSize: 14, fontWeight: 500 }}>
                  {importing ? 'Import en cours…' : 'Glissez votre fichier .xlsx ici'}
                </div>
                {!importing && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>ou cliquez pour choisir un fichier</div>}
                <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: 'none' }}
                  onChange={e => handleImportFile(e.target.files?.[0])} />
              </div>
            )}

            {/* Erreur */}
            {importError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13 }}>
                {importError}
              </div>
            )}

            {/* Résultat */}
            {importResult && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ color: '#15803d', fontWeight: 700, fontSize: 14 }}>✓ Import réussi</div>
                {importResult.period && <div style={{ color: '#166534', fontSize: 13 }}>Période : {importResult.period}</div>}
                <div style={{ color: '#374151', fontSize: 13 }}>
                  {importResult.imported} ligne{importResult.imported !== 1 ? 's' : ''} importée{importResult.imported !== 1 ? 's' : ''}
                  {importResult.created > 0 && ` · ${importResult.created} nouveau${importResult.created !== 1 ? 'x' : ''} streamer${importResult.created !== 1 ? 's' : ''} créé${importResult.created !== 1 ? 's' : ''}`}
                  {importResult.updated > 0 && ` · ${importResult.updated} mis à jour`}
                  {importResult.skipped > 0 && ` · ${importResult.skipped} ignoré${importResult.skipped !== 1 ? 's' : ''}`}
                  {importResult.deleted > 0 && <span style={{ color: '#ef4444' }}> · {importResult.deleted} supprimé{importResult.deleted !== 1 ? 's' : ''}</span>}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              {importResult && (
                <button onClick={() => { setImportResult(null); setImportError(null); }} style={btnGhost}>
                  Importer un autre fichier
                </button>
              )}
              <button onClick={() => { setShowImport(false); setImportResult(null); setImportError(null); }} style={btnPrimary}>
                {importResult ? 'Fermer' : 'Annuler'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal création ── */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={e => { if (e.target === e.currentTarget) setShowCreate(false); }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ color: '#0f172a', fontWeight: 700, fontSize: 18 }}>Nouveau streamer</div>

            <LightField label="Nom affiché">
              <input style={inputLight} value={newDisplayName} placeholder="ex: Haribo Live" autoFocus
                onChange={e => setNewDisplayName(e.target.value)} />
            </LightField>
            <LightField label="@Username TikTok">
              <input style={inputLight} value={newUsername} placeholder="ex: haribofr"
                onChange={e => setNewUsername(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreate()} />
            </LightField>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={() => setShowCreate(false)} style={btnGhost}>Annuler</button>
              <button onClick={handleCreate} disabled={creating || !newUsername.trim() || !newDisplayName.trim()} style={{ ...btnPrimary, opacity: (!newUsername.trim() || !newDisplayName.trim()) ? 0.5 : 1 }}>
                {creating ? 'Création…' : 'Créer le streamer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const MEDALS: Record<string, string> = { 'Agent': '🥇', 'Co-Leader': '🥈', 'Recruteur': '🥉' };

function MedalOverlay({ role, size = 40 }: { role?: string | null; size?: number }) {
  if (!role || !MEDALS[role]) return null;
  const fs = size <= 40 ? 13 : size <= 50 ? 15 : 18;
  return (
    <span style={{
      position: 'absolute', bottom: -3, right: -3,
      fontSize: fs, lineHeight: 1,
      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
      pointerEvents: 'none',
    }}>{MEDALS[role]}</span>
  );
}

function StreamerCard({ streamer: s, onSelect, objectives }: { streamer: Streamer; onSelect: (s: Streamer) => void; objectives: Objectives }) {
  const initials = s.display_name.slice(0, 2).toUpperCase();
  const isLive = !!s.tiktok_live;
  return (
    <div onClick={() => onSelect(s)}
      style={{ background: '#fff', border: `1px solid ${isLive ? '#fecaca' : '#e2e8f0'}`, borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', boxShadow: isLive ? '0 0 0 2px rgba(220,38,38,0.1)' : '0 1px 3px rgba(0,0,0,0.05)' } as React.CSSProperties}
      onTouchStart={e => (e.currentTarget as HTMLDivElement).style.background = '#f0f9ff'}
      onTouchEnd={e => (e.currentTarget as HTMLDivElement).style.background = '#fff'}>
      {/* Avatar */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <LiveAvatar url={s.avatar_url} initials={initials} size={48} isLive={isLive} />
        <MedalOverlay role={s.leader_role} size={48} />
      </div>
      {/* Infos + jauge */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name}</div>
        <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{s.username}</div>
        {s.diamonds != null && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
            {s.diamonds.toLocaleString('fr-FR')} 💎
            <span style={{ color: '#cbd5e1', margin: '0 5px' }}>·</span>
            {s.live_days ?? 0}j
            <span style={{ color: '#cbd5e1', margin: '0 5px' }}>·</span>
            {fmtDuration(s.live_duration_minutes)}
          </div>
        )}
        <div style={{ marginTop: 6 }}>
          <ObjectiveGaugeMobile s={s} obj={objectives} />
        </div>
      </div>
      {/* Chevron */}
      <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="#cbd5e1" strokeWidth="2" style={{ flexShrink: 0 }}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );
}

const fmtDuration = (mins?: number | null) => {
  if (!mins) return '—';
  return `${Math.floor(mins / 60)}h ${mins % 60}min`;
};
const fmtNum = (n?: number | null) => n != null ? n.toLocaleString('fr-FR') : '—';
const fmtEchelon = (s?: string | null) => {
  if (!s) return '—';
  return s.replace(/^[ÉE]chelon\s*/i, '').replace(/^non\s+maintenu$/i, 'Non maintenu');
};

function StreamerRow({ streamer: s, onSelect, isAdmin, onDelete, objectives }: {
  streamer: Streamer; onSelect: (s: Streamer) => void; isAdmin: boolean; onDelete: () => void; objectives: Objectives;
}) {
  const [hover, setHover] = useState(false);
  const initials = s.display_name.slice(0, 2).toUpperCase();

  const tiktokUrl = s.tiktok_live
    ? `https://www.tiktok.com/@${s.username}/live`
    : `https://www.tiktok.com/@${s.username}`;

  return (
    <div onClick={() => onSelect(s)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{ display: 'grid', gridTemplateColumns: isAdmin ? '1fr 130px 110px 70px 60px 80px 80px 80px 60px' : '1fr 130px 110px 70px 60px 80px 80px 60px', gap: 8, padding: '12px 16px', borderRadius: 10, background: hover ? '#fff' : 'transparent', border: `1px solid ${hover ? '#e2e8f0' : 'transparent'}`, cursor: 'pointer', alignItems: 'center', transition: 'all 0.12s', boxShadow: hover ? '0 1px 4px rgba(0,0,0,0.06)' : 'none' }}>

      {/* Jauge + Avatar + nom */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <ObjectiveGauge s={s} obj={objectives} size={40} />
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <LiveAvatar url={s.avatar_url} initials={initials} size={40} isLive={!!s.tiktok_live} />
          <MedalOverlay role={s.leader_role} size={40} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#0f172a', fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.display_name}</div>
        </div>
      </div>

      {/* @username */}
      <div style={{ color: '#64748b', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{s.username}</div>

      {/* Diamants */}
      <div style={{ color: '#0f172a', fontSize: 13, fontWeight: s.diamonds ? 600 : 400 }}>{fmtNum(s.diamonds)}</div>

      {/* Durée live */}
      <div style={{ color: '#64748b', fontSize: 13 }}>{fmtDuration(s.live_duration_minutes)}</div>

      {/* Jours live */}
      <div style={{ color: '#64748b', fontSize: 13 }}>{s.live_days != null ? s.live_days : '—'}</div>

      {/* Rémunération Montée */}
      <div style={{ color: (s.remu_niveau || 0) > 0 ? '#15803d' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
        {(s.remu_niveau || 0) > 0 ? `$${s.remu_niveau!.toFixed(2)}` : '—'}
      </div>

      {/* Rémunération Activité */}
      <div style={{ color: (s.remu_activite || 0) > 0 ? '#2563eb' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
        {(s.remu_activite || 0) > 0 ? `$${s.remu_activite!.toFixed(2)}` : '—'}
      </div>

      {/* Rémunération Progressif (admin only) */}
      {isAdmin && (
        <div style={{ color: (s.remu_progressif || 0) > 0 ? '#7c3aed' : '#94a3b8', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
          {(s.remu_progressif || 0) > 0 ? `$${s.remu_progressif!.toFixed(2)}` : '—'}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }} onClick={e => e.stopPropagation()}>
        {/* Bouton TikTok */}
        <a href={tiktokUrl} target="_blank" rel="noreferrer"
          title={s.tiktok_live ? 'Rejoindre le live' : 'Voir le profil TikTok'}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: s.tiktok_live ? '#dc2626' : '#f1f5f9', border: 'none', cursor: 'pointer', textDecoration: 'none', flexShrink: 0, transition: 'background 0.15s' }}
          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = s.tiktok_live ? '#b91c1c' : '#e2e8f0'}
          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = s.tiktok_live ? '#dc2626' : '#f1f5f9'}>
          <TikTokIcon color={s.tiktok_live ? '#fff' : '#374151'} size={14} />
        </a>
        {/* Bouton supprimer (admin) */}
        {isAdmin && (
          <button onClick={onDelete}
            title="Supprimer le streamer"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 14, transition: 'all 0.15s', flexShrink: 0 }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#fef2f2'; (e.currentTarget as HTMLElement).style.color = '#ef4444'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = '#94a3b8'; }}>
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

// ── FilterChip ───────────────────────────────────────────

function FilterChip({ active, color, bg, small, onClick, children }: {
  active: boolean; color: string; bg: string; small?: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick} style={{
      padding: small ? '5px 8px' : '6px 11px', borderRadius: 20, border: `1.5px solid ${active ? color : '#e2e8f0'}`,
      background: active ? bg : '#fff', color: active ? color : '#64748b',
      fontSize: small ? 11 : 12, fontWeight: active ? 600 : 400, cursor: 'pointer',
      whiteSpace: 'nowrap', transition: 'all 0.15s', flexShrink: 0,
    }}>
      {children}
    </button>
  );
}

// ── Jauges objectifs ─────────────────────────────────────

function objColor(pct: number): string {
  if (pct >= 1)   return '#16a34a'; // vert
  if (pct >= 0.5) return '#ea580c'; // orange
  return '#dc2626';                  // rouge
}

function objMetrics(s: Streamer, obj: Objectives) {
  return [
    {
      value: s.live_days ?? 0,
      target: obj.days,
      label: `Jours live : ${s.live_days ?? 0} / ${obj.days}`,
    },
    {
      value: Math.floor((s.live_duration_minutes ?? 0) / 60),
      target: obj.hours,
      label: `Heures live : ${Math.floor((s.live_duration_minutes ?? 0) / 60)} / ${obj.hours}h`,
    },
    {
      value: s.diamonds ?? 0,
      target: obj.diamonds,
      label: `Diamants : ${(s.diamonds ?? 0).toLocaleString('fr-FR')} / ${obj.diamonds.toLocaleString('fr-FR')} 💎`,
    },
  ];
}

/** Thermomètre vertical — 3 cases empilées, à gauche de l'avatar (desktop) */
function ObjectiveGauge({ s, obj, size }: { s: Streamer; obj: Objectives; size: number }) {
  if (s.diamonds == null && s.live_days == null) {
    return <div style={{ width: 10, flexShrink: 0 }} />;
  }
  const metrics = objMetrics(s, obj);
  const cellH = Math.floor((size - 4) / 3); // 2 gaps × 2px
  const tooltip = metrics.map(m => m.label).join('\n');

  return (
    <div title={tooltip} style={{ display: 'flex', flexDirection: 'column', gap: 2, width: 8, height: size, flexShrink: 0, cursor: 'default' }}>
      {[...metrics].reverse().map((m, i) => {
        const pct = Math.min(1, m.target > 0 ? m.value / m.target : 0);
        const radius = i === 0 ? '4px 4px 0 0' : i === 2 ? '0 0 4px 4px' : '0';
        return (
          <div key={i} style={{
            height: cellH, background: objColor(pct),
            borderRadius: radius, transition: 'background 0.4s',
          }} />
        );
      })}
    </div>
  );
}

/** Thermomètre horizontal — 3 cases côte à côte, en bas de la carte (mobile) */
function ObjectiveGaugeMobile({ s, obj }: { s: Streamer; obj: Objectives }) {
  if (s.diamonds == null && s.live_days == null) return null;
  const metrics = objMetrics(s, obj);
  const tooltip = metrics.map(m => m.label).join('\n');

  return (
    <div title={tooltip} style={{ width: '100%', display: 'flex', gap: 2, cursor: 'default' }}>
      {metrics.map((m, i) => {
        const pct = Math.min(1, m.target > 0 ? m.value / m.target : 0);
        const radius = i === 0 ? '4px 0 0 4px' : i === 2 ? '0 4px 4px 0' : '0';
        return (
          <div key={i} style={{
            flex: 1, height: 6, background: objColor(pct),
            borderRadius: radius, transition: 'background 0.4s',
          }} />
        );
      })}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────

const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Juin','Juil','Aoû','Sep','Oct','Nov','Déc'];
function formatPeriodLabel(p: string): string {
  // "2026-04" → "Avril 2026"
  const mYM = p.match(/^(\d{4})-(\d{2})$/);
  if (mYM) {
    const month = MONTHS_FR[parseInt(mYM[2]) - 1];
    return `${month} ${mYM[1]}`;
  }
  // "2026-05-01 ~ 2026-05-07" → "Mai 2026"
  const mRange = p.match(/^(\d{4})-(\d{2})-\d{2}\s*~\s*\d{4}-\d{2}-\d{2}$/);
  if (mRange) {
    const month = MONTHS_FR[parseInt(mRange[2]) - 1];
    return `${month} ${mRange[1]}`;
  }
  return p;
}

function TikTokIcon({ color = '#000', size = 16 }: { color?: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.73a4.85 4.85 0 0 1-1.01-.04z"/>
    </svg>
  );
}

function LightField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{ color: '#374151', fontSize: 13, fontWeight: 500 }}>{label}</div>
      {children}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  color: '#94a3b8', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: 0.8,
};

const summaryLabelStyle: React.CSSProperties = {
  color: '#94a3b8', fontSize: 10, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 1,
};

const inputLight: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #e2e8f0', background: '#fff',
  color: '#0f172a', fontSize: 13, boxSizing: 'border-box',
  outline: 'none',
};

const btnPrimary: React.CSSProperties = {
  padding: '9px 18px', borderRadius: 8, border: 'none',
  background: '#2563eb', color: '#fff', fontSize: 13,
  fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
};

const btnGhost: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8, border: '1px solid #e2e8f0',
  background: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer',
};
